import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { paisa } from '@fleet/shared';
import { useDuesQuery } from '@/api/endpoints';
import type { Due } from '@/api/types';
import { Button } from '@/design/Button';
import { Figure, MoneyText } from '@/design/Data';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import { Panel } from '@/design/Panel';
import { PaymentModal } from './PaymentModal';
import { RaiseInvoiceModal } from './RaiseInvoiceModal';

/**
 * What is owed.
 *
 * A chasing list, not a report. Every column exists because it answers one of
 * the four questions somebody asks while reaching for the phone: who, how much,
 * how long, and have we sent them anything in writing.
 *
 * Oldest first, because that is the order debts get chased in. The phone number
 * is a link rather than text, because on the phone this is used on, chasing
 * means tapping it.
 *
 * A booking only appears once the trip has actually happened. A confirmed
 * booking for next month is not a debt, and a list that says otherwise is a
 * list nobody opens twice.
 */

function ageTone(days: number): 'neutral' | 'attention' | 'alert' {
  if (days >= 30) return 'alert';
  if (days >= 7) return 'attention';
  return 'neutral';
}

export function DuesPage(): ReactElement {
  const { data, isLoading, isError, refetch } = useDuesQuery();
  const [payFor, setPayFor] = useState<Due | null>(null);
  const [advanceFor, setAdvanceFor] = useState<Due | null>(null);

  // One modal, two doors into it. Which door decides whether it opens as a
  // payment against a debt or an advance on a trip that has not happened.
  const active: { row: Due; advance: boolean } | null =
    payFor !== null
      ? { row: payFor, advance: false }
      : advanceFor !== null
        ? { row: advanceFor, advance: true }
        : null;

  /** Raising asks for the customer's tax number first. See RaiseInvoiceModal. */
  const [raisingFor, setRaisingFor] = useState<Due | null>(null);

  const columns: readonly Column<Due>[] = [
    {
      key: 'who',
      header: 'Customer',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.customer_name}</span>
          <a
            href={`tel:${row.customer_phone.replace(/[^\d+]/g, '')}`}
            className="identifier text-micro text-ink-muted underline-offset-2 hover:text-ink hover:underline"
          >
            {row.customer_phone}
          </a>
        </div>
      ),
    },
    {
      key: 'booking',
      header: 'Booking',
      render: (row) => (
        <div className="flex flex-col">
          <Link
            to={`/bookings/${row.booking}`}
            className="identifier underline-offset-2 hover:underline"
          >
            {row.reference}
          </Link>
          {row.vehicle_name !== null && (
            <span className="text-micro text-ink-muted">{row.vehicle_name}</span>
          )}
        </div>
      ),
    },
    {
      key: 'age',
      header: 'Back',
      render: (row) => {
        const tone = ageTone(row.days_since_return);
        const label =
          row.days_since_return === 0
            ? 'Today'
            : row.days_since_return === 1
              ? 'Yesterday'
              : `${String(row.days_since_return)} days ago`;
        return (
          <span
            className={
              tone === 'alert'
                ? 'font-medium text-alert'
                : tone === 'attention'
                  ? 'text-attention'
                  : 'text-ink-muted'
            }
          >
            {label}
          </span>
        );
      },
    },
    {
      key: 'total',
      header: 'Booking total',
      numeric: true,
      secondary: true,
      render: (row) => <MoneyText amount={paisa(row.total)} />,
    },
    {
      key: 'paid',
      header: 'Paid',
      numeric: true,
      secondary: true,
      render: (row) =>
        row.paid === 0 ? (
          <span className="text-ink-faint">—</span>
        ) : (
          <MoneyText amount={paisa(row.paid)} />
        ),
    },
    {
      key: 'owed',
      header: 'Due',
      numeric: true,
      render: (row) => (
        <span className="font-semibold">
          <MoneyText amount={paisa(row.owed)} />
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button intent="primary" onClick={() => setPayFor(row)}>
            Take payment
          </Button>
          {row.invoiced ? (
            <span className="self-center text-micro uppercase text-ink-muted">Invoiced</span>
          ) : (
            <Button onClick={() => setRaisingFor(row)}>Invoice</Button>
          )}
        </div>
      ),
    },
  ];

  /**
   * Trips still to come, and what has been taken against them.
   *
   * Deliberately a second table rather than more rows in the first: these are
   * not debts, and mixing them in is what would make the chasing list above
   * untrustworthy. They are here because an advance is taken *before* a trip,
   * and there was previously nowhere in the app to record the deposit somebody
   * had just handed over.
   */
  const upcomingColumns: readonly Column<Due>[] = [
    columns[0]!,
    columns[1]!,
    {
      key: 'when',
      header: 'Goes out',
      render: (row) => (
        <span className="text-ink-muted">
          {row.days_since_return === 0
            ? 'Today'
            : row.days_since_return === 1
              ? 'Tomorrow'
              : `In ${String(row.days_since_return)} days`}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Price',
      numeric: true,
      secondary: true,
      render: (row) =>
        row.total === 0 ? (
          <span className="text-attention">Not quoted</span>
        ) : (
          <MoneyText amount={paisa(row.total)} />
        ),
    },
    {
      key: 'advance',
      header: 'Advance taken',
      numeric: true,
      render: (row) =>
        row.paid === 0 ? (
          <span className="text-ink-faint">None</span>
        ) : (
          <span className="font-semibold">
            <MoneyText amount={paisa(row.paid)} />
          </span>
        ),
    },
    {
      // "Take more" was the only thing this button ever said once an advance
      // had been taken, which is how QA came to report that there was no way
      // to finish a due. There always was — the modal opens on the remaining
      // balance — but nothing on the screen said so. Now the button says what
      // pressing it will do, and says nothing at all when there is nothing
      // left to take.
      key: 'actions',
      header: '',
      render: (row) => {
        const left = row.total - row.paid;
        if (row.total > 0 && left <= 0) {
          return (
            <div className="flex justify-end">
              <span className="text-micro uppercase text-ink-muted">Paid in full</span>
            </div>
          );
        }
        return (
          <div className="flex justify-end">
            <Button intent="primary" onClick={() => setAdvanceFor(row)}>
              {row.paid === 0 ? 'Take advance' : 'Settle the rest'}
            </Button>
          </div>
        );
      },
    },
  ];

  const rows = data?.rows ?? [];
  const upcoming = data?.upcoming ?? [];
  const oldest = rows.reduce((worst, row) => Math.max(worst, row.days_since_return), 0);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">Dues</h1>

      {/* The numbers somebody wants before reading a single row. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Figure
          label="Outstanding"
          value={<MoneyText amount={paisa(data?.total_owed ?? 0)} />}
          emphasis={(data?.total_owed ?? 0) > 0 ? 'attention' : 'neutral'}
        />
        <Figure label="Bookings" value={String(data?.count ?? 0)} />
        <Figure
          label="Oldest"
          value={oldest === 0 ? '—' : `${String(oldest)} days`}
          emphasis={oldest >= 30 ? 'alert' : 'neutral'}
        />
        <Figure
          label="Advances held"
          value={<MoneyText amount={paisa(data?.total_advanced ?? 0)} />}
        />
      </div>

      <Panel
        title="Owed to the business"
        note="Trips that have happened and are not settled. Oldest first — that is the order to ring in."
        current
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.booking}
          rowHref={(row) => `/bookings/${row.booking}`}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          errorHeadline="The dues did not load"
          errorDetail="The server did not answer. Check the connection and load them again."
          empty={{
            headline: 'Nothing is owed',
            detail:
              'Every trip that has happened is paid for. Bookings still to come are not counted here — they are not debts yet.',
          }}
        />
      </Panel>

      <Panel
        title="Coming up"
        note="Confirmed trips that have not gone out yet. Not owed — this is where an advance is taken, or the rest of one settled."
      >
        <DataTable
          columns={upcomingColumns}
          rows={upcoming}
          rowKey={(row) => row.booking}
          rowHref={(row) => `/bookings/${row.booking}`}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          errorHeadline="The upcoming bookings did not load"
          errorDetail="The server did not answer. Check the connection and load them again."
          empty={{
            headline: 'Nothing booked ahead',
            detail:
              'Confirmed bookings that have not started yet appear here, so a deposit can be taken against them before the keys go out.',
          }}
        />
      </Panel>

      <RaiseInvoiceModal
        booking={
          raisingFor === null
            ? null
            : { id: raisingFor.booking, reference: raisingFor.reference }
        }
        customerPan={raisingFor?.customer_pan ?? ''}
        onRaised={() => setRaisingFor(null)}
        onClose={() => setRaisingFor(null)}
      />

      <PaymentModal
        booking={
          active === null
            ? null
            : {
                id: active.row.booking,
                reference: active.row.reference,
                customerName: active.row.customer_name,
                owed: active.advance ? active.row.total - active.row.paid : active.row.owed,
                isAdvance: active.advance,
              }
        }
        onClose={() => {
          setPayFor(null);
          setAdvanceFor(null);
        }}
      />
    </div>
  );
}
